import React, { useState } from "react";
import { Grid, GridColumn, GridNoRecords } from "@progress/kendo-react-grid";
import {
    DateFormatCell,
    AmountFormatCellEmptyZero,
} from "components/common/formatter";
import styles from "./LedgerGrid.module.scss";
import { useEffect } from "react";
import { exportData } from "components/Report/exportUtility";
import { ExportConfigs } from "components/Report/exportConfigs";
import PreviousTransactionsGrid from "./MatterPreviousTransactions";
import {
    cellWithBackGround,
    RefCell,
    CenterTextHeaderCell,
    SettingsHeaderCell,
    ToolbarHeaderCell,
} from "./LedgerGridCells";
import { GridLoader } from "components/GridLoader";

const LedgerGridContent = ({
    data,
    showDDA,
    handleActionRef,
    isPrevTrans,
    component,
    exportReport,
    matterId,
    onShowPrevTrans,
    onPrint,
    onToggleDDA,
    onToggleShowReversals,
    showDDAValue,
    showReversalsValue,
    isClosed,
    isExporting,
    loading,
    gridKey,
}) => {
    return (
        <Grid
            key={gridKey}
            loader={
                <div style={{height: "500px" }}>
                    <GridLoader />
                </div>
            }
            showLoader={loading}
            scroll
            data={data}
            dataItemKey="postingInternalId"
            className="h-100 grid-fs13"
        >
            <GridNoRecords>
                {loading ? (
                    <span> </span>
                ) : (
                    data && data?.length === 0 && "No records available"
                )}
            </GridNoRecords>
            <GridColumn
                width="150px"
                headerCell={(props) =>
                    ToolbarHeaderCell({
                        ...props,
                        onPrint,
                        onShowPrevTrans,
                        isClosed,
                        isExporting,
                        matterId,
                        exportReport,
                    })
                }
            >
                <GridColumn
                    field="date"
                    title="Date"
                    width="100px"
                    cell={DateFormatCell}
                />
                <GridColumn field="postingType" title="Type" width="60px" />
                <GridColumn
                    field="reference"
                    title="Ref"
                    width="100px"
                    cell={(props) => RefCell({ ...props, isPrevTrans }, handleActionRef)}
                    className="text-wrap"
                />
                <GridColumn
                    field="details"
                    title="Details"
                    width="310px"
                    className="text-prewrap"
                />
                <GridColumn
                    field="vatAmount"
                    title="VAT"
                    width="90px"
                    headerCell={CenterTextHeaderCell}
                    cell={AmountFormatCellEmptyZero}
                />
            </GridColumn>

            <GridColumn title="OFFICE" headerCell={CenterTextHeaderCell}>
                <GridColumn
                    field="grossAmount"
                    title="Debit"
                    headerCell={CenterTextHeaderCell}
                    cell={(e) => cellWithBackGround(e, "OFFICE", "Debit")}
                    width="95px"
                />
                <GridColumn
                    field="grossAmount"
                    title="Credit"
                    headerCell={CenterTextHeaderCell}
                    cell={(e) => cellWithBackGround(e, "OFFICE", "Credit")}
                    width="95px"
                />
                <GridColumn
                    field="balance"
                    title="Balance"
                    headerCell={CenterTextHeaderCell}
                    cell={(e) => cellWithBackGround(e, "OFFICE", "Amount")}
                    width="100px"
                />
            </GridColumn>
            <GridColumn title="CLIENT" headerCell={CenterTextHeaderCell}>
                <GridColumn
                    field="clientAmount"
                    title="Debit"
                    headerCell={CenterTextHeaderCell}
                    cell={(e) => cellWithBackGround(e, "CLIENT", "Debit")}
                    width="95px"
                />
                <GridColumn
                    field="clientAmount"
                    title="Credit"
                    headerCell={CenterTextHeaderCell}
                    cell={(e) => cellWithBackGround(e, "CLIENT", "Credit")}
                    width="95px"
                />
                <GridColumn
                    field="clientBalance"
                    title="Balance"
                    headerCell={CenterTextHeaderCell}
                    cell={(e) => cellWithBackGround(e, "CLIENT", "Amount")}
                    width="100px"
                />
            </GridColumn>
            {showDDA && (
                <GridColumn title="DDA" headerCell={CenterTextHeaderCell}>
                    <GridColumn
                        field="ddaAmount"
                        title="Debit"
                        headerCell={CenterTextHeaderCell}
                        cell={(e) => cellWithBackGround(e, "DDA", "Debit")}
                        width="95px"
                    />
                    <GridColumn
                        field="ddaAmount"
                        title="Credit"
                        headerCell={CenterTextHeaderCell}
                        cell={(e) => cellWithBackGround(e, "DDA", "Credit")}
                        width="95px"
                    />
                    <GridColumn
                        field="ddaBalance"
                        title="Balance"
                        headerCell={CenterTextHeaderCell}
                        cell={(e) => cellWithBackGround(e, "DDA", "Amount")}
                        width="100px"
                    />
                </GridColumn>
            )}
            <GridColumn
                width="10px"
                cells={{
                    headerCell: (gridHeaderProps) => (
                        <SettingsHeaderCell
                            {...gridHeaderProps}
                            showDDAValue={showDDAValue}
                            onToggleDDA={onToggleDDA}
                            showReversalsValue={showReversalsValue}
                            onToggleShowReversals={onToggleShowReversals}
                        />
                    ),
                }}
            ></GridColumn>
        </Grid>
    );
};

export default function MatterGrid(props) {
    const [showPrevTrans, setShowPrevTrans] = useState(false);
    const [gridData, setGridData] = useState([]);
    const [fixedScroll, setFixedScroll] = useState(props.fixedScroll);
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        if (props?.gridData) {
            setGridData(props.gridData);
        }
    }, [props?.gridData]);

    useEffect(() => {
        setFixedScroll(props.fixedScroll);
    }, [props.fixedScroll]);

    const exportReport = async (isPreviousTransactions = false) => {
        try {
            setIsExporting(true);

            await exportData({
                ...ExportConfigs.MATTER_LEDGER,
                requestBody: ExportConfigs.MATTER_LEDGER.getRequestBody(
                    props.matterId,
                    isPreviousTransactions
                ),
                fileName: props?.matterRef || "Ledger",
                onStart: () => setIsExporting(true),
                onComplete: () => setIsExporting(false),
                onError: () => setIsExporting(false),
            });
        } catch (error) {
            setIsExporting(false);
        }
    };

    return (
        <>
            <div className="h-100">
                <>
                    <LedgerGridContent
                        gridKey={props.gridKey}
                        loading={props.loading}
                        component={props.comp}
                        data={gridData}
                        showDDA={props.showDDA}
                        handleActionRef={props.handleActionRef}
                        isPrevTrans={false}
                        matterId={props.matterId}
                        onShowPrevTrans={() => {
                            setShowPrevTrans(true);
                        }}
                        onPrint={() => {
                            if (props.matterId) props.setOpenReport(true);
                        }}
                        onToggleDDA={() => props.setShowDDA((prev) => !prev)}
                        onToggleShowReversals={() =>
                            props.setShowReversals?.((prev) => !prev)
                        }
                        showDDAValue={props.showDDA}
                        showReversalsValue={props.showReversals}
                        isClosed={props?.isClosed}
                        exportReport={exportReport}
                        isExporting={isExporting}
                    />

                    {showPrevTrans && (
                        <PreviousTransactionsGrid
                            entity={{
                                matterId: props.matterId,
                                matterRef: props.matterRef,
                            }}
                            title={props.prevTransTitle || "Previous Transactions"}
                            isOpen={showPrevTrans}
                            onClose={() => setShowPrevTrans(false)}
                            showDDA={props.showDDA}
                            onExport={exportReport}
                            isExporting={isExporting}
                            handleActionRef={props.handleActionRef}
                        />
                    )}
                </>
            </div>
        </>
    );
}
